var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { html } from 'lit';
import { customElement, property, query, queryAll, state } from 'lit/decorators.js';
import styles from './header.styles';
import { classMap } from 'lit/directives/class-map.js';
import { dictionary } from '@mf-cms/helpers';
import { MFElement } from '../mfelement/mfelement';
let PHeader = class PHeader extends MFElement {
    constructor() {
        super(...arguments);
        this.layout = 'default';
        this.navAlign = 'right';
        this.menu = [];
        this.cultures = [];
        this.searchExpanded = false;
        this.culture = 'en-GB';
        this.showUser = false;
        this.siteType = '';
        this.languageListExpanded = false;
        this.userListExpanded = false;
        this.mobileNavExpanded = false;
        this.overflowStartIndex = -1;
        this.itemWidths = [];
        this.moreButtonWidth = 0;
        this.navGap = 0;
        this.rafId = null;
        this.setupComplete = false;
        this.breakpointListenerConnected = false;
        this.boundHandleBreakpointChange = () => {
            if (!this.setupComplete)
                return;
            this.measureNav().then(() => this.recomputeOverflow());
        };
    }
    static { this.styles = styles; }
    /**
     * Connected callback lifecycle. On reconnect (setupComplete already true),
     * re-attaches the overflow observers torn down by disconnectedCallback —
     * firstUpdated only fires on the first connect.
     * @return {void}
     */
    connectedCallback() {
        super.connectedCallback();
        this.handleDocumentClick = this.handleDocumentClick.bind(this);
        if (this.setupComplete) {
            void this.initOverflowDetection();
        }
    }
    /**
     * First updated lifecycle. Initial overflow-detection setup runs here (not in
     * connectedCallback) because it depends on the first render having produced
     * the wrapper element that the ResizeObserver observes.
     * @returns {Promise<void>} - Resolves once the initial measurement pass completes
     */
    async firstUpdated() {
        if (typeof window === 'undefined')
            return;
        await this.initOverflowDetection();
    }
    /**
     * Updated lifecycle. Re-measures when the menu changes after initial setup
     * has completed. The setupComplete gate prevents racing with the initial async
     * measurement pass started inside firstUpdated.
     * @param {Map<string, unknown>} changed - Changed properties
     * @returns {void}
     */
    updated(changed) {
        if (typeof window === 'undefined')
            return;
        if (this.setupComplete && changed.has('menu')) {
            this.measureNav().then(() => this.recomputeOverflow());
        }
    }
    /**
     * Disconnected callback lifecycle. Tears down the overflow observers.
     * @returns {void}
     */
    disconnectedCallback() {
        super.disconnectedCallback();
        if (this.rafId !== null) {
            cancelAnimationFrame(this.rafId);
            this.rafId = null;
        }
        this.teardownOverflowObservers();
    }
    /**
     * Attaches overflow observers and runs a measurement pass. Used for both the
     * initial setup (from firstUpdated) and reconnects (from connectedCallback).
     * @returns {Promise<void>} - Resolves once measurement completes
     */
    async initOverflowDetection() {
        await this.setupOverflowObservers();
        await this.measureNav();
        this.recomputeOverflow();
        this.setupComplete = true;
    }
    /**
     * Ensures resize and breakpoint observers are attached.
     * Safe to call multiple times.
     * @returns {Promise<void>} - Resolves when the observer is attached to the current wrapper element
     */
    async setupOverflowObservers() {
        if (typeof window === 'undefined')
            return;
        if (!this.mobileBreakpoint) {
            this.mobileBreakpoint = window.matchMedia('(max-width: 768px)');
        }
        if (!this.breakpointListenerConnected) {
            this.mobileBreakpoint.addEventListener('change', this.boundHandleBreakpointChange);
            this.breakpointListenerConnected = true;
        }
        if (!this.resizeObserver) {
            this.resizeObserver = new ResizeObserver(() => this.scheduleRecompute());
        }
        await this.updateComplete;
        if (this.wrapperEl) {
            this.resizeObserver.observe(this.wrapperEl);
        }
    }
    /**
     * Detaches overflow observers.
     * @returns {void}
     */
    teardownOverflowObservers() {
        this.resizeObserver?.disconnect();
        if (this.mobileBreakpoint && this.breakpointListenerConnected) {
            this.mobileBreakpoint.removeEventListener('change', this.boundHandleBreakpointChange);
            this.breakpointListenerConnected = false;
        }
    }
    /**
     * Schedules a recomputeOverflow on the next animation frame, coalescing rapid resize events.
     * @returns {void}
     */
    scheduleRecompute() {
        if (this.rafId !== null)
            cancelAnimationFrame(this.rafId);
        this.rafId = requestAnimationFrame(() => {
            this.rafId = null;
            this.recomputeOverflow();
        });
    }
    /**
     * Measures each top-level nav item's natural width and the More button's width.
     * Renders the menu in two passes (full menu, then with More forced) so all widths
     * can be captured. The cached widths drive recomputeOverflow on each resize.
     * @returns {Promise<void>} - Resolves when the cached widths are populated
     */
    async measureNav() {
        if (typeof window === 'undefined')
            return;
        if (this.mobileBreakpoint?.matches) {
            // At mobile widths the nav is block-level (items full-width) and .more-li
            // is display:none — any widths read here would be wrong for desktop. Skip
            // and let boundHandleBreakpointChange re-run measureNav on return.
            return;
        }
        if (!this.menu || this.menu.length === 0) {
            this.itemWidths = [];
            this.moreButtonWidth = 0;
            return;
        }
        this.overflowStartIndex = -1;
        await this.updateComplete;
        if (!this.navUl)
            return;
        const style = getComputedStyle(this.navUl);
        this.navGap = parseFloat(style.columnGap || style.gap || '0') || 0;
        const liElements = Array.from(this.navUl.querySelectorAll(':scope > li'));
        this.itemWidths = liElements
            .filter((li) => !li.classList.contains('more-li'))
            .map((li) => li.getBoundingClientRect().width);
        this.overflowStartIndex = Math.max(0, this.menu.length - 1);
        await this.updateComplete;
        const moreLi = this.navUl.querySelector(':scope > li.more-li');
        if (moreLi) {
            this.moreButtonWidth = moreLi.getBoundingClientRect().width;
        }
    }
    /**
     * Computes how many top-level nav items fit in the available nav budget and
     * sets overflowStartIndex accordingly. Skipped on mobile widths where the mobile
     * menu replaces the desktop nav.
     * @returns {void}
     */
    recomputeOverflow() {
        if (typeof window === 'undefined')
            return;
        if (this.mobileBreakpoint?.matches) {
            if (this.overflowStartIndex !== -1)
                this.overflowStartIndex = -1;
            return;
        }
        if (!this.navUl || this.itemWidths.length === 0)
            return;
        const navWidth = this.navUl.parentElement?.offsetWidth ?? 0;
        if (navWidth <= 0)
            return;
        let allFitWidth = 0;
        for (let i = 0; i < this.itemWidths.length; i++) {
            if (i > 0)
                allFitWidth += this.navGap;
            allFitWidth += this.itemWidths[i];
        }
        if (allFitWidth <= navWidth) {
            if (this.overflowStartIndex !== -1)
                this.overflowStartIndex = -1;
            return;
        }
        const budget = navWidth - this.moreButtonWidth - this.navGap;
        let cumulative = 0;
        let cutoff = 0;
        for (let i = 0; i < this.itemWidths.length; i++) {
            if (i > 0)
                cumulative += this.navGap;
            cumulative += this.itemWidths[i];
            if (cumulative > budget) {
                cutoff = i;
                break;
            }
            cutoff = i + 1;
        }
        if (cutoff !== this.overflowStartIndex) {
            this.overflowStartIndex = cutoff;
        }
    }
    /**
     * Recursively checks whether a menu item or any descendant is the active page.
     * @param {MenuItem} item - Menu item to inspect
     * @returns {boolean} - True if the item or any descendant has active === true
     */
    containsActiveItem(item) {
        if (item.active)
            return true;
        if (item.children && item.children.length > 0) {
            return item.children.some((c) => this.containsActiveItem(c));
        }
        return false;
    }
    /**
     * Handle outside clicks
     * @param {MouseEvent} event - event
     * @return {void}
     */
    handleDocumentClick(event) {
        const path = event.composedPath();
        // Close language list if clicked outside
        if (!path.includes(this.langNav) && this.languageListExpanded) {
            this.languageListExpanded = false;
            document.removeEventListener('mousedown', this.handleDocumentClick);
        }
        // Close user list if clicked outside
        if (!path.includes(this.userNav) && this.userListExpanded) {
            this.userListExpanded = false;
            document.removeEventListener('mousedown', this.handleDocumentClick);
        }
    }
    /**
     * Toggle language list
     * @return {void}
     */
    toggleLanguageList() {
        if (this.languageListExpanded) {
            document.removeEventListener('mousedown', this.handleDocumentClick);
        }
        else {
            document.addEventListener('mousedown', this.handleDocumentClick);
            // Close open submenus
            this.navExpandButtons.forEach((button) => {
                button.setAttribute('aria-expanded', 'false');
            });
            this.navExpandLinklessButtons.forEach((button) => {
                button.setAttribute('aria-expanded', 'false');
            });
        }
        this.languageListExpanded = !this.languageListExpanded;
    }
    /**
     * Toggle user list
     * @return {void}
     */
    toggleUserList() {
        if (this.userListExpanded) {
            document.removeEventListener('mousedown', this.handleDocumentClick);
        }
        else {
            document.addEventListener('mousedown', this.handleDocumentClick);
            // Close open submenus
            this.navExpandButtons.forEach((button) => {
                button.setAttribute('aria-expanded', 'false');
            });
            this.navExpandLinklessButtons.forEach((button) => {
                button.setAttribute('aria-expanded', 'false');
            });
        }
        this.userListExpanded = !this.userListExpanded;
    }
    /**
     * Toggle submenu
     * @param {Event} e - Event
     * @return {void}
     */
    toggleSubMenu(e) {
        const button = e.target;
        const expanded = button.getAttribute('aria-expanded') === 'true';
        button.setAttribute('aria-expanded', expanded ? 'false' : 'true');
    }
    /**
     * Close all submenus on focus out
     * @param {any} e - Event
     * @return {void}
     */
    subMenuFocusOut(e) {
        if (!e.currentTarget.contains(e.relatedTarget)) {
            this.navExpandButtons.forEach((button) => {
                button.setAttribute('aria-expanded', 'false');
            });
            this.navExpandLinklessButtons.forEach((button) => {
                button.setAttribute('aria-expanded', 'false');
            });
        }
    }
    /**
     * Toggle search
     * @return {void}
     */
    toggleSearch() {
        // Focus search input if expanding
        if (!this.searchExpanded) {
            this.mobileNavExpanded = false;
            setTimeout(() => {
                if (this.searchInput) {
                    this.searchInput.setFocus();
                }
            }, 10);
        }
        this.searchExpanded = !this.searchExpanded;
    }
    /**
     * Toggle mobile navigation
     * @return {void}
     */
    toggleMobileNavigation() {
        // Set focus
        if (!this.mobileNavExpanded) {
            setTimeout(() => {
                this.mainNav.querySelector('a')?.focus();
            }, 10);
        }
        this.mobileNavExpanded = !this.mobileNavExpanded;
    }
    /**
     * Get active culture
     * @return {Culture} Active culture
     **/
    getActiveCulture() {
        return this.cultures.find((x) => x.active) ?? this.cultures[0];
    }
    /**
     * Handle keydown
     * @param {KeyboardEvent} e - event
     * @return {void}
     */
    handleKeydown(e) {
        if (e.key === 'Escape') {
            this.userListExpanded = false;
            this.languageListExpanded = false;
            this.navExpandButtons.forEach((button) => {
                button.setAttribute('aria-expanded', 'false');
            });
            this.navExpandLinklessButtons.forEach((button) => {
                button.setAttribute('aria-expanded', 'false');
            });
        }
    }
    /**
     * Trigger search
     * @param {KeyboardEvent} e - event
     * @return {void}
     */
    triggerSearch(e) {
        if (this.designMode || this.editMode)
            return;
        if (e.key === 'Enter') {
            this.searchForm.submit();
        }
    }
    /**
     * Get Menu page Children Items
     *
     * @param {MenuItem} child - Menu item to iterate over
     * @returns {*} - html
     */
    getMenuPageChildrenItems(child) {
        return html ` <li class=${classMap({ selected: child.active, [`has-sub`]: child.children && child.children.length > 0
        })}
      >
        ${this.renderLink(child)}
        ${child.children && child.children.length > 0
            ? html ` <button class="expand-sub" aria-label=${dictionary(this.culture, 'header', 'expandSubmenu', [child.name])} aria-expanded="false" aria-controls=${'sub-' + child.guid} @click=${this.toggleSubMenu} > <svg aria-hidden="true" xmlns="http://www.w3.org/2000/svg" width="11" height="11" fill="currentColor" class="caret" viewBox="0 0 16 16" > <path fill-rule="evenodd" d="M1.646 4.646a.5.5 0 0 1 .708 0L8 10.293l5.646-5.647a.5.5 0 0 1 .708.708l-6 6a.5.5 0 0 1-.708 0l-6-6a.5.5 0 0 1 0-.708z" /> </svg> </button> <ul id="sub-${child.guid}"> ${child.children.map((child2) => this.getMenuPageChildrenItems(child2))} </ul> `
            : ''}
      </li>
    `;
    }
    /**
     * Render component
     * @returns {TemplateResult} Template
     */
    render() {
        const classes = {
            [`${this.layout}-layout`]: true,
            [`nav-${this.navAlign}`]: true,
            contained: this.contained,
            edit: this.editMode
        };
        return html ` <header class=${classMap(classes)} @keydown=${this.handleKeydown}> <div class="wrapper"> <div class="logo"> ${this.logo ? html `
                  <a href=${this.getActiveCulture()?.url}>
                    <img src=${this.logo} alt="Logo" />
                  </a>
                `
            : ''}
          </div>
          ${this.renderNavigation()} ${this.renderOptionsArea()} ${this.renderSearch()}
        </div>
      </header>
    `;
    }
    canSearch() {
        const siteSearchSettings = JSON.parse(this.siteSearchSettings);
        return siteSearchSettings.searchIncludePages || siteSearchSettings.searchIncludeFiles;
    }
    renderSearch() {
        if (!this.canSearch())
            return;
        return html `<div class="search${classMap({ show: this.searchExpanded })}" id="search"> <div class="search-wrap" role="search"> <form action=${this.searchPage} method="get" id="search-form"> <!-- special solution to be able to post the value with a form since shadow dom input does not work in form we could also do a search input component with embeded form or not use a component for inputs --> <input type="hidden" value=${this.searchQuery} id="search-value" name="q" /> <p-input value=${this.searchQuery} @keypress=${(e) => this.triggerSearch(e)} @p-input=${() => (this.searchValue.value = this.searchInput.value)} type="search" label-hidden placeholder=${this.siteType == 'play' ? dictionary(this.culture, 'header', 'searchPlaceholderVideo') : dictionary(this.culture, 'header', 'searchPlaceholder')} label=${dictionary(this.culture, 'header', 'search')} input-id="main-search" id="search-input" > <svg slot="prefix" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" class="bi bi-search" viewBox="0 0 16 16" > <path d="M11.742 10.344a6.5 6.5 0 1 0-1.397 1.398h-.001q.044.06.098.115l3.85 3.85a1 1 0 0 0 1.415-1.414l-3.85-3.85a1 1 0 0 0-.115-.1zM12 6.5a5.5 5.5 0 1 1-11 0 5.5 5.5 0 0 1 11 0" /> </svg> </p-input> </form> </div> </div>`;
    }
    renderOptionsArea() {
        return html ` <nav class="options" aria-label="Options"> <ul> ${this.renderLanguageSelect()} ${this.showUser ? html `
                <li class="user-li">
                  <button
                    @click=${this.toggleUserList}
                    aria-expanded=${this.userListExpanded ? 'true' : 'false'}
                    aria-label=${dictionary(this.culture, 'header', 'loggedInAs')}
                    aria-controls="user-nav"
                  >
                    <svg
                      aria-hidden="true"
                      xmlns="http://www.w3.org/2000/svg"
                      width="19"
                      height="19"
                      fill="currentColor"
                      class="bi bi-person"
                      viewBox="0 0 16 16"
                    >
                      <path
                        d="M8 8a3 3 0 1 0 0-6 3 3 0 0 0 0 6m2-3a2 2 0 1 1-4 0 2 2 0 0 1 4 0m4 8c0 1-1 1-1 1H3s-1 0-1-1 1-4 6-4 6 3 6 4m-1-.004c-.001-.246-.154-.986-.832-1.664C11.516 10.68 10.289 10 8 10s-3.516.68-4.168 1.332c-.678.678-.83 1.418-.832 1.664z"
                      />
                    </svg>
                    <svg
                      aria-hidden="true"
                      xmlns="http://www.w3.org/2000/svg"
                      width="11"
                      height="11"
                      fill="currentColor"
                      class="caret"
                      viewBox="0 0 16 16"
                    >
                      <path
                        fill-rule="evenodd"
                        d="M1.646 4.646a.5.5 0 0 1 .708 0L8 10.293l5.646-5.647a.5.5 0 0 1 .708.708l-6 6a.5.5 0 0 1-.708 0l-6-6a.5.5 0 0 1 0-.708z"
                      />
                    </svg>
                  </button>
                  <ul
                    id="user-nav"
                    @focusout=${(e) => (this.userListExpanded = e.currentTarget.contains(e.relatedTarget))}
                  >
                    <li>
                      <a href="/logout">${dictionary(this.culture, 'header', 'logout')}</a>
                    </li>
                  </ul>
                </li>
              `
            : ''}
          ${this.layout === 'default' && this.canSearch()
            ? html ` <li> <button @click=${this.toggleSearch} aria-label=${dictionary(this.culture, 'header', 'showSearch')} aria-expanded=${this.searchExpanded ? 'true' : 'false'} aria-controls="search" > <svg aria-hidden="true" xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" class="bi bi-search" viewBox="0 0 16 16" > <path d="M11.742 10.344a6.5 6.5 0 1 0-1.397 1.398h-.001q.044.06.098.115l3.85 3.85a1 1 0 0 0 1.415-1.414l-3.85-3.85a1 1 0 0 0-.115-.1zM12 6.5a5.5 5.5 0 1 1-11 0 5.5 5.5 0 0 1 11 0" /> </svg> </button> </li> `
            : ''}
          ${this.collectionItems > 0
            ? html ` <li> <button aria-label=${`${dictionary(this.culture, 'header', 'collection')}, ${this.collectionItems} ${this.collectionItems > 1 ? dictionary(this.culture, 'header', 'items') : dictionary(this.culture, 'header', 'item')}`}
                  >
                    <svg
                      aria-hidden="true"
                      xmlns="http://www.w3.org/2000/svg"
                      width="16"
                      height="16"
                      fill="currentColor"
                      class="bi bi-bag"
                      viewBox="0 0 16 16"
                    >
                      <path
                        d="M8 1a2.5 2.5 0 0 1 2.5 2.5V4h-5v-.5A2.5 2.5 0 0 1 8 1m3.5 3v-.5a3.5 3.5 0 1 0-7 0V4H1v10a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V4zM2 5h12v9a1 1 0 0 1-1 1H3a1 1 0 0 1-1-1z"
                      />
                    </svg>
                    <span aria-hidden="true" class="collection-count">${this.collectionItems}</span>
                  </button>
                </li>
              `
            : ''}
          <li class="mobile-nav-li">
            <button
              @click=${this.toggleMobileNavigation}
              class="toggle-mobile-nav"
              aria-label=${dictionary(this.culture, 'header', 'showMenu')}
              aria-expanded=${this.mobileNavExpanded ? 'true' : 'false'}
            ></button>
          </li>
        </ul>
      </nav>
    `;
    }
    renderLanguageSelect() {
        if (!this.cultures || this.cultures.length <= 1)
            return html ``;
        return html ` <li class="lang-li"> <button @click=${this.toggleLanguageList} aria-expanded=${this.languageListExpanded ? 'true' : 'false'} aria-controls="lang-nav" aria-label=${`${dictionary(this.culture, 'header', 'selectedLanguage')} ${this.getActiveCulture()?.name}`}
          class="lang"
        >
          <svg
            aria-hidden="true"
            xmlns="http://www.w3.org/2000/svg"
            width="16"
            height="16"
            fill="currentColor"
            class="bi bi-globe"
            viewBox="0 0 16 16"
          >
            <path
              d="M0 8a8 8 0 1 1 16 0A8 8 0 0 1 0 8m7.5-6.923c-.67.204-1.335.82-1.887 1.855A8 8 0 0 0 5.145 4H7.5zM4.09 4a9.3 9.3 0 0 1 .64-1.539 7 7 0 0 1 .597-.933A7.03 7.03 0 0 0 2.255 4zm-.582 3.5c.03-.877.138-1.718.312-2.5H1.674a7 7 0 0 0-.656 2.5zM4.847 5a12.5 12.5 0 0 0-.338 2.5H7.5V5zM8.5 5v2.5h2.99a12.5 12.5 0 0 0-.337-2.5zM4.51 8.5a12.5 12.5 0 0 0 .337 2.5H7.5V8.5zm3.99 0V11h2.653c.187-.765.306-1.608.338-2.5zM5.145 12q.208.58.468 1.068c.552 1.035 1.218 1.65 1.887 1.855V12zm.182 2.472a7 7 0 0 1-.597-.933A9.3 9.3 0 0 1 4.09 12H2.255a7 7 0 0 0 3.072 2.472M3.82 11a13.7 13.7 0 0 1-.312-2.5h-2.49c.062.89.291 1.733.656 2.5zm6.853 3.472A7 7 0 0 0 13.745 12H11.91a9.3 9.3 0 0 1-.64 1.539 7 7 0 0 1-.597.933M8.5 12v2.923c.67-.204 1.335-.82 1.887-1.855q.26-.487.468-1.068zm3.68-1h2.146c.365-.767.594-1.61.656-2.5h-2.49a13.7 13.7 0 0 1-.312 2.5m2.802-3.5a7 7 0 0 0-.656-2.5H12.18c.174.782.282 1.623.312 2.5zM11.27 2.461c.247.464.462.98.64 1.539h1.835a7 7 0 0 0-3.072-2.472c.218.284.418.598.597.933M10.855 4a8 8 0 0 0-.468-1.068C9.835 1.897 9.17 1.282 8.5 1.077V4z"
            />
          </svg>
          <span aria-hidden="true"> ${this.getActiveCulture()?.name} </span>
          <svg
            aria-hidden="true"
            xmlns="http://www.w3.org/2000/svg"
            width="11"
            height="11"
            fill="currentColor"
            class="caret"
            viewBox="0 0 16 16"
          >
            <path
              fill-rule="evenodd"
              d="M1.646 4.646a.5.5 0 0 1 .708 0L8 10.293l5.646-5.647a.5.5 0 0 1 .708.708l-6 6a.5.5 0 0 1-.708 0l-6-6a.5.5 0 0 1 0-.708z"
            />
          </svg>
        </button>
        <ul
          id="lang-nav"
          @focusout=${(e) => (this.languageListExpanded = e.currentTarget.contains(e.relatedTarget))}
        >
          ${this.renderLanguageOptions()}
        </ul>
      </li>
    `;
    }
    renderLanguageOptions() {
        return this.cultures.map((culture) => {
            return html ` <li class=${classMap({ selected: culture.active })}> <a aria-current=${culture.active ? 'page' : 'false'} href=${culture.url} >${culture.name}</a > </li> `;
        });
    }
    renderNavigation() {
        return html ` <nav class="nav${classMap({ show: this.mobileNavExpanded })}" aria-label=${dictionary(this.culture, 'header', 'mainMenu')} > <ul> ${this.renderNavigationItems()} </ul> </nav> `;
    }
    renderNavigationItems() {
        const visible = this.overflowStartIndex === -1 ? this.menu : this.menu.slice(0, this.overflowStartIndex);
        const overflow = this.overflowStartIndex === -1 ? [] : this.menu.slice(this.overflowStartIndex);
        return html ` ${visible.map((page) => this.renderTopLevelItem(page))} ${overflow.length > 0 ? this.renderMoreItem(overflow) : ''} `;
    }
    /**
     * Renders a single top-level nav item with its dropdown (when applicable).
     * @param {MenuItem} page - Top-level menu item
     * @returns {TemplateResult} - Rendered <li>
     */
    renderTopLevelItem(page) {
        return html ` <li class=${classMap({ selected: page.active, [`linkless`]: !!page.pageType && page.pageType == 'videos',
            [`has-sub`]: page.children && page.children.length > 0
        })}
      >
        ${this.renderNavigationLink(page)}
        ${page.children && page.children.length > 0
            ? html ` <ul id="sub-${page.guid}" @focusout=${this.subMenuFocusOut}> ${page.children.map((child) => this.getMenuPageChildrenItems(child))} </ul> `
            : ''}
      </li>
    `;
    }
    /**
     * Renders the "More..." dropdown that holds menu items pushed out of the visible nav.
     * Children of overflow items render as flat siblings inside the dropdown, indented by depth.
     * @param {MenuItem[]} overflow - Menu items to host inside the More dropdown
     * @returns {TemplateResult} - Rendered <li>
     */
    renderMoreItem(overflow) {
        const moreLabel = dictionary(this.culture, 'header', 'more');
        const anyActive = overflow.some((p) => this.containsActiveItem(p));
        return html ` <li class=${classMap({ 'has-sub': true, linkless: true, 'more-li': true, selected: anyActive })} > <button class="linkless-expand" aria-label=${moreLabel} aria-expanded="false" aria-controls="sub-more" @click=${this.toggleSubMenu} > ${moreLabel} <span class="caret-container"> <svg aria-hidden="true" xmlns="http://www.w3.org/2000/svg" width="11" height="11" fill="currentColor" class="caret" viewBox="0 0 16 16" > <path fill-rule="evenodd" d="M1.646 4.646a.5.5 0 0 1 .708 0L8 10.293l5.646-5.647a.5.5 0 0 1 .708.708l-6 6a.5.5 0 0 1-.708 0l-6-6a.5.5 0 0 1 0-.708z" /> </svg> </span> </button> <ul id="sub-more" @focusout=${this.subMenuFocusOut}> ${overflow.map((page) => this.renderMoreEntry(page, 0))} </ul> </li> `;
    }
    /**
     * Recursively flattens an overflow menu item and its descendants into sibling <li>s
     * inside the More dropdown. Indentation is communicated via the --more-depth custom
     * property which the stylesheet uses to compute the link's left padding.
     * @param {MenuItem} page - Menu item to render
     * @param {number} depth - Nesting depth (0 for the top-level overflow item)
     * @returns {TemplateResult} - Rendered <li> plus any descendant <li>s
     */
    renderMoreEntry(page, depth) {
        return html ` <li class=${classMap({ selected: page.active, 'more-indent': depth > 0 })} style=${`--more-depth: ${depth};`}
      >
        ${this.renderLink({ active: page.active, url: this.getPageUrl(page), name: page.name })}
      </li>
      ${page.children && page.children.length > 0
            ? page.children.map((child) => this.renderMoreEntry(child, depth + 1))
            : ''}
    `;
    }
    /**
     * Resolves the href for a menu item. Root pages in non-default cultures point
     * at the culture's prefix (`/${culture}`) rather than the page's stored path,
     * matching how the main nav link resolves its URL.
     * @param {MenuItem} page - Menu item to resolve
     * @returns {string} - Resolved URL
     */
    getPageUrl(page) {
        if (page.type == 'root' && !page.isDefaultCulture)
            return `/${this.culture}`;
        return page.url;
    }
    renderNavigationLink(page) {
        if (page.pageType == 'videos') {
            return html ` <button class="linkless-expand" aria-label=${dictionary(this.culture, 'header', 'expandSubmenu', [page.name])} aria-expanded="false" aria-current=${page.active} aria-controls=${'sub-' + page.guid} @click=${this.toggleSubMenu} > ${page.name} <span class="caret-container"> <svg aria-hidden="true" xmlns="http://www.w3.org/2000/svg" width="11" height="11" fill="currentColor" class="caret" viewBox="0 0 16 16" > <path fill-rule="evenodd" d="M1.646 4.646a.5.5 0 0 1 .708 0L8 10.293l5.646-5.647a.5.5 0 0 1 .708.708l-6 6a.5.5 0 0 1-.708 0l-6-6a.5.5 0 0 1 0-.708z" /> </svg> </span> </button> `;
        }
        const link = { active: page.active, url: this.getPageUrl(page), name: page.name };
        return html ` ${this.renderLink(link)} ${this.renderNavLinkChildren(page)} `;
    }
    renderNavLinkChildren(page) {
        if (!page.children || page.children.length <= 0)
            return html ``;
        return html ` <button class="expand-sub" aria-label=${dictionary(this.culture, 'header', 'expandSubmenu', [page.name])} aria-expanded="false" aria-controls=${'sub-' + page.guid} @click=${this.toggleSubMenu} > <svg aria-hidden="true" xmlns="http://www.w3.org/2000/svg" width="11" height="11" fill="currentColor" class="caret" viewBox="0 0 16 16" > <path fill-rule="evenodd" d="M1.646 4.646a.5.5 0 0 1 .708 0L8 10.293l5.646-5.647a.5.5 0 0 1 .708.708l-6 6a.5.5 0 0 1-.708 0l-6-6a.5.5 0 0 1 0-.708z" /> </svg> </button> `;
    }
    renderLink(link) {
        if (this.designMode)
            return html `<a onclick="return false;" aria-current=${link.active ? 'page' : 'false'} href=${link.url} > ${link.name} </a>`;
        return html `<a aria-current=${link.active ? 'page' : 'false'} href=${link.url}> ${link.name} </a>`;
    }
};
__decorate([
    property({ type: String, reflect: true })
], PHeader.prototype, "layout", void 0);
__decorate([
    property({ type: String, reflect: true, attribute: 'nav-align' })
], PHeader.prototype, "navAlign", void 0);
__decorate([
    property({ type: String, reflect: true })
], PHeader.prototype, "logo", void 0);
__decorate([
    property({ type: Boolean, reflect: true })
], PHeader.prototype, "contained", void 0);
__decorate([
    property({ type: Boolean, reflect: true, attribute: 'edit-mode' })
], PHeader.prototype, "editMode", void 0);
__decorate([
    property({ type: Boolean, reflect: true, attribute: 'design-mode' })
], PHeader.prototype, "designMode", void 0);
__decorate([
    property({ type: Number, reflect: true, attribute: 'collection-items' })
], PHeader.prototype, "collectionItems", void 0);
__decorate([
    property({ type: Array, reflect: true })
], PHeader.prototype, "menu", void 0);
__decorate([
    property({ type: Array, reflect: true })
], PHeader.prototype, "cultures", void 0);
__decorate([
    property({ type: String, reflect: true, attribute: 'site-search-settings' })
], PHeader.prototype, "siteSearchSettings", void 0);
__decorate([
    property({ type: String, reflect: true, attribute: 'search-page' })
], PHeader.prototype, "searchPage", void 0);
__decorate([
    property({ type: String, reflect: true, attribute: 'search-query' })
], PHeader.prototype, "searchQuery", void 0);
__decorate([
    property({ type: Boolean, reflect: true, attribute: 'search-expanded' })
], PHeader.prototype, "searchExpanded", void 0);
__decorate([
    property({ type: String })
], PHeader.prototype, "culture", void 0);
__decorate([
    property({ type: Boolean, reflect: true, attribute: 'show-user' })
], PHeader.prototype, "showUser", void 0);
__decorate([
    property({ type: String, reflect: true, attribute: 'site-type' })
], PHeader.prototype, "siteType", void 0);
__decorate([
    state()
], PHeader.prototype, "languageListExpanded", void 0);
__decorate([
    state()
], PHeader.prototype, "userListExpanded", void 0);
__decorate([
    state()
], PHeader.prototype, "mobileNavExpanded", void 0);
__decorate([
    state()
], PHeader.prototype, "overflowStartIndex", void 0);
__decorate([
    query('.lang-li')
], PHeader.prototype, "langNav", void 0);
__decorate([
    query('.user-li')
], PHeader.prototype, "userNav", void 0);
__decorate([
    query('.nav')
], PHeader.prototype, "mainNav", void 0);
__decorate([
    query('.wrapper')
], PHeader.prototype, "wrapperEl", void 0);
__decorate([
    query('.nav > ul')
], PHeader.prototype, "navUl", void 0);
__decorate([
    query('#search-input')
], PHeader.prototype, "searchInput", void 0);
__decorate([
    queryAll('.expand-sub')
], PHeader.prototype, "navExpandButtons", void 0);
__decorate([
    queryAll('.linkless-expand')
], PHeader.prototype, "navExpandLinklessButtons", void 0);
__decorate([
    query('#search-form')
], PHeader.prototype, "searchForm", void 0);
__decorate([
    query('#search-value')
], PHeader.prototype, "searchValue", void 0);
PHeader = __decorate([
    customElement('p-header')
    /**
     * Header component
     *
     * @class PHeader
     * @typedef {PHeader}
     * @extends {LitElement}
     */
], PHeader);
export { PHeader };
